import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SpecificationsDisplayGrid } from "core/components/SpecificationsDisplayGrid";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EstimateDamageLocation } from "../entities";
import { useEstimateRequestEnumTranslations } from "../hooks/useEstimateRequestEnumTranslations";
import { ImagePreview } from "./ImagePreview";

export type EstimateRequestDisplayProps = {
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  vinNumber?: string;
  vinImageSrc?: string;
  email: string;
  firstName: string;
  lastName: string;
  notes: string;
  phoneNumber: string;
  shop: ReactNode;
  damages: {
    location: EstimateDamageLocation;
    images: { src: string; alt: string }[];
  }[];
  paymentMethod: string;
  insurerName: string;
  PreContentSlot?: ReactNode;
  PostContentSlot?: ReactNode;
};

export function EstimateRequestDisplay({
  vehicleMake,
  vehicleModel,
  vehicleYear,
  vinNumber,
  vinImageSrc,
  email,
  firstName,
  lastName,
  notes,
  phoneNumber,
  shop,
  damages,
  paymentMethod,
  insurerName,
  PreContentSlot,
  PostContentSlot,
}: EstimateRequestDisplayProps) {
  const enumTranslations = useEstimateRequestEnumTranslations();
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:EstimateRequestDisplay",
  });
  return (
    <>
      {PreContentSlot}
      <Box
        gap={2}
        pb={2}
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
      >
        <Paper sx={{ p: 2, overflow: "auto" }} component="section">
          <Typography variant="h5" mb={2}>
            {t("vehicleInformation")}
          </Typography>
          <SpecificationsDisplayGrid
            boxProps={{ mb: 1 }}
            t={t}
            fields={{
              vehicleMake,
              vehicleModel,
              vehicleYear,
              vinNumber,
            }}
          />

          {vinImageSrc && (
            <Box m={0} component="figure">
              <Typography
                component="figcaption"
                variant="body2"
                color="primary.dark"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                {t("vin")}
              </Typography>
              <ImagePreview
                src={vinImageSrc}
                alt={t("vinImageAltText")}
                width={175}
                height={(175 / 3) * 2}
              />
            </Box>
          )}
        </Paper>
        <Paper sx={{ p: 2, overflow: "auto" }} component="section">
          <Typography variant="h5" mb={2}>
            {t("customerInformation")}
          </Typography>
          <SpecificationsDisplayGrid
            t={t}
            fields={{
              email,
              firstName,
              lastName,
              notes,
              phoneNumber,
              shop,
              paymentMethod,
              insurerName,
            }}
          />
        </Paper>
        <Paper
          sx={{ p: 2, gridColumn: "1/-1", overflow: "auto" }}
          component="section"
        >
          <Typography variant="h5" mb={2}>
            {t("damageInformation")}
          </Typography>
          <Box
            display="grid"
            gap={4}
            gridTemplateColumns="repeat(auto-fit, minmax(min(300px,100%),1fr))"
          >
            {damages.map(({ images, location }) => (
              <Box m={0} component="figure" key={location}>
                <Typography
                  component="figcaption"
                  variant="body2"
                  color="primary.dark"
                  sx={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  {enumTranslations.EstimateImageLocation[location]}
                </Typography>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  {images.map((image) => (
                    <ImagePreview
                      width={200}
                      height={(200 / 3) * 2}
                      key={image.src}
                      src={image.src}
                      alt={image.alt}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
      {PostContentSlot}
    </>
  );
}
